import React, { useState } from 'react';
import { Modal, Button, Typography, Alert, message } from 'antd';
import { createPanic } from '../utils/apiService';
import { setPanicId } from '../utils/cookieUtils';
import { getCurrentLocation } from '../utils/locationUtils';
import { API_CONFIG } from '../utils/constants';

const { Text } = Typography;

/**
 * Panic confirmation modal
 *
 * Props:
 * - visible: boolean
 * - onCancel: function
 * - onSubmitted: function(panicId)
 */
const PanicConfirmModal = ({ visible, onCancel, onSubmitted }) => {
  const [submitting, setSubmitting] = useState(false);

  const reverseGeocode = async (lat, lng) => {
    try {
      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.GOOGLEGEOCODE.REVERSE_GEOCODE}?latitude=${lat}&longitude=${lng}`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      const resolvedAddress = data.custom_formatted_address || data.placeId;
      return resolvedAddress;
    } catch (e) {
      console.warn('Reverse geocode failed', e);
      return '';
    }
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      // 1) Get current location
      const { latitude, longitude } = await getCurrentLocation();

      // 2) Try reverse geocoding to get a human-readable address
      const address = await reverseGeocode(latitude, longitude);

      // 3) Create Panic
      const payload = {
        latitude: String(latitude),
        longitude: String(longitude),
        address: address || ''
      };
      const res = await createPanic(payload);
      const id = res?.panicId || res?.id;
      if (!id) throw new Error('No Panic ID returned');

      // 4) Persist id and notify parent
      setPanicId(id);
      onSubmitted?.(id);
    } catch (err) {
      console.error('CreatePanic failed', err);
      message.error('Failed to submit Panic. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Confirm Panic"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button key="confirm" type="primary" danger onClick={handleConfirm} loading={submitting}>
          Confirm Panic
        </Button>
      ]}
      closable={!submitting}
      maskClosable={false}
    >
      <Alert
        message="Emergency Response Confirmation"
        description={
          <div>
            <Text>
              By confirming, you acknowledge that this is an emergency and a Panic will be sent immediately.
              Your live location will be attached, and emergency services will be dispatched immediately.
            </Text>
            <br />
            <Text type="secondary">
              Make sure your location services are enabled and accurate on your device.
            </Text>
          </div>
        }
        type="warning"
        showIcon
      />
    </Modal>
  );
};

export default PanicConfirmModal;
